import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosHttp from "services/axiosHttp";
import { message } from "antd";

export const initialState = {
	loading: false,
	error: null,
	territoriesByRegion: [],
};

export const createTerritory = createAsyncThunk("territory/createTerritory", async (territoryData, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.post("territory/create", territoryData);
		message.success("Territory created successfully!");
		return response.data;
	} catch (error) {
		message.error(error.response?.data?.message || "Failed to create territory");
		return rejectWithValue(error.response?.data || error.message);
	}
});

export const fetchTerritoryDetail = createAsyncThunk("territory/fetchTerritoryDetail", async (territoryId, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`territory/detail/${territoryId}`);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

export const fetchTerritoriesByRegion = createAsyncThunk("territory/fetchTerritoriesByRegion", async (payload, { rejectWithValue }) => {
	try {
		const regionId = typeof payload === "object" ? payload?.regionId : payload;
		const keyword = typeof payload === "object" ? payload?.keyword : "";

		const params = new URLSearchParams();
		if (keyword?.trim()) {
			params.append("keyword", keyword.trim());
		}

		const queryString = params.toString();
		const url = queryString ? `territory/by-region/${regionId}?${queryString}` : `territory/by-region/${regionId}`;
		const response = await axiosHttp.get(url);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

export const updateTerritory = createAsyncThunk("territory/updateTerritory", async ({ territoryId, data }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.patch(`territory/update?id=${territoryId}`, data);
		message.success("Territory updated successfully!");
		return response.data;
	} catch (error) {
		message.error(error.response?.data?.message || "Failed to update territory");
		return rejectWithValue(error.response?.data || error.message);
	}
});

export const deleteTerritory = createAsyncThunk("territory/deleteTerritory", async (territoryId, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.delete(`territory/${territoryId}`);
		message.success("Territory deleted successfully!");
		return response.data;
	} catch (error) {
		message.error(error.response?.data?.message || "Failed to delete territory");
		return rejectWithValue(error.response?.data || error.message);
	}
});

export const assignSalesRep = createAsyncThunk("territory/assignSalesRep", async (data, { rejectWithValue }) => {
	try {
		const payload = {
			...data,
			role_type: "SALES_REP",
		};
		const response = await axiosHttp.post("sales-assignment/create", payload);
		message.success("Sales representative assigned successfully!");
		return response.data;
	} catch (error) {
		message.error(error.response?.data?.message || "Failed to assign sales representative");
		return rejectWithValue(error.response?.data || error.message);
	}
});

export const unassignSalesRep = createAsyncThunk("territory/unassignSalesRep", async (assignmentId, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`sales-assignment/delete/${assignmentId}`);
		message.success("Sales representative unassigned successfully!");
		return response.data;
	} catch (error) {
		message.error(error.response?.data?.message || "Failed to unassign sales representative");
		return rejectWithValue(error.response?.data || error.message);
	}
});

const territorySlice = createSlice({
	name: "territory",
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(createTerritory.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createTerritory.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(createTerritory.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		builder
			.addCase(updateTerritory.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateTerritory.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(updateTerritory.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		builder
			.addCase(deleteTerritory.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteTerritory.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(deleteTerritory.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		builder
			.addCase(assignSalesRep.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(assignSalesRep.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(assignSalesRep.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		builder
			.addCase(unassignSalesRep.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(unassignSalesRep.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(unassignSalesRep.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		builder
			.addCase(fetchTerritoriesByRegion.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchTerritoriesByRegion.fulfilled, (state, action) => {
				state.loading = false;
				state.territoriesByRegion = action.payload;
			})
			.addCase(fetchTerritoriesByRegion.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});
	},
});

export const { clearError } = territorySlice.actions;

export default territorySlice.reducer;
